/**
 * WebVitalsProvider - 性能监控组件
 *
 * 🎯 功能：
 * 1. 监测所有 Web Vitals 核心指标（LCP/CLS/INP/FCP/TTFB）
 * 2. 控制台输出元素详情，可点击跳转到 DevTools
 * 3. 显示简洁的深色监控面板
 *
 * 📦 使用方式：
 * ```tsx
 * import WebVitalsProvider from '@/components/Common/WebVitalsProvider';
 *
 * function App({ children }) {
 *   return (
 *     <WebVitalsProvider>
 *       {children}
 *     </WebVitalsProvider>
 *   );
 * }
 * ```
 */

import { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useWebVitals } from '@/hooks/useWebVitals';

// 动态导入面板组件（避免 SSR 问题）
const WebVitalsPanel = dynamic(() => import('./WebVitalsPanel'), { ssr: false });

interface WebVitalsProviderProps {
  children: ReactNode;
  /**
   * 是否启用 Web Vitals 监控
   * @default 只在开发/测试环境启用
   */
  enabled?: boolean;
}

/**
 * Web Vitals Provider 组件
 *
 * 环境检测：
 * - development: ✅ 启用
 * - test: ✅ 启用
 * - production: ❌ 禁用（除非手动设置 enabled={true}）
 */
export default function WebVitalsProvider({
  children,
  enabled = process.env.NODE_ENV !== 'production'
}: WebVitalsProviderProps) {
  // 生产环境默认禁用
  if (!enabled) {
    return <>{children}</>;
  }

  return <WebVitalsProviderInner>{children}</WebVitalsProviderInner>;
}

/**
 * 内部组件：实际的监控逻辑
 */
function WebVitalsProviderInner({ children }: { children: ReactNode }) {
  // 启用 Web Vitals 监测
  useWebVitals();

  return (
    <>
      {children}
      {/* 性能监控面板 */}
      <WebVitalsPanel />
    </>
  );
}
